import { cn } from "@/lib/utils";
import type { TransitionClasses, TransitionEvents } from "@headlessui/react";
import {
  Dialog as HeadlessDialog,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import type { ElementType, ReactNode } from "react";
import { Fragment } from "react";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  TitleComponent?: ElementType;
  DialogComponent?: ElementType;
  SubtitleComponent?: ElementType;
  CloseButton?: ElementType;
  dialogTransitionProps?: TransitionClasses & TransitionEvents;
  backdropTransitionProps?: TransitionClasses & TransitionEvents;
  alignment?: "top" | "center" | "bottom";
  classNames?: {
    container?: string;
    backdrop?: string;
    panel?: string;
    panelHeader?: string;
    title?: string;
    subtitle?: string;
  };
}

export const Dialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  TitleComponent = "h2",
  DialogComponent = "div",
  SubtitleComponent,
  dialogTransitionProps,
  backdropTransitionProps,
  alignment = "center",
  CloseButton,
  classNames,
}: DialogProps) => {
  const Description = subtitle && !SubtitleComponent ? "p" : SubtitleComponent;

  return (
    <Transition appear show={open} as={Fragment}>
      <HeadlessDialog
        as={DialogComponent}
        static
        open={open}
        onClose={onClose}
        className={cn("relative z-50", classNames?.container)}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          {...backdropTransitionProps}
        >
          <div
            className={cn("fixed inset-0 bg-black/25", classNames?.backdrop)}
          />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-2"
          {...dialogTransitionProps}
        >
          <div
            className={cn(
              "fixed inset-0 flex h-full justify-center",
              alignment === "top" && "items-start",
              alignment === "center" && "items-center",
              alignment === "bottom" && "items-end",
            )}
          >
            <HeadlessDialog.Panel
              className={cn(
                "relative m-4 w-full max-w-md bg-white py-4 text-center",
                classNames?.panel,
              )}
            >
              <div className={cn("px-6", classNames?.panelHeader)}>
                <HeadlessDialog.Title
                  as={TitleComponent}
                  className={classNames?.title}
                >
                  {title}
                </HeadlessDialog.Title>
                {subtitle || SubtitleComponent ? (
                  <HeadlessDialog.Description
                    as={Description}
                    className={classNames?.subtitle}
                  >
                    {subtitle}
                  </HeadlessDialog.Description>
                ) : null}
                {CloseButton && <CloseButton />}
              </div>
              {children}
            </HeadlessDialog.Panel>
          </div>
        </TransitionChild>
      </HeadlessDialog>
    </Transition>
  );
};
